// features/admin/setCreditApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { adminrefreshToken } from '../../utils/adminRefreshToken.ts';

export interface UserCredit {
  id?: number;
  user_id: number;
  credit_limit: number;
  balance: number;
  created_at?: string;
  updated_at?: string;
  // User details from join
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
}

export interface CreditApiResponse<T> {
  error: boolean;
  message: string;
  result?: T;
  data?: T;
}

export interface SetCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
}

export interface UpdateCreditRequest {
  user_id: number;
  credit_limit: number;
  balance?: number;
}

export interface DeleteCreditRequest {
  user_id: number;
}

export interface CreditUser {
  user_id: number;
  first_name: string | null;
  last_name: string | null;
  email?: string | null;
  phone?: string | null;
  credit_limit: string;
  balance: string;
}

// New interfaces for user search
export interface SearchUserRequest {
  search?: string;
  limit?: number;
}

export interface SearchUser {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

export const CREDIT_API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com',
  ENDPOINTS: {
    SET_CREDIT: '/admin/xx',
    LIST_CREDIT_USERS: '/admin/CreditlistUsers', 
    UPDATE_CREDIT: '/admin/setCredit',
    DELETE_CREDIT: '/admin/deleteCredit',
    GET_USER_CREDIT: '/admin/getUserCredit',
    SEARCH_USERS: '/admin/searchUsers', 
  },
};

export const creditSalesApi = createApi({
  reducerPath: 'setCreditApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CREDIT_API_CONFIG.BASE_URL,
    prepareHeaders: async (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      
      try {
        await adminrefreshToken();
      } catch (err) {
        console.error('Error refreshing token:', err);
      }

      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['UserCredit', 'CreditUsers', 'SearchUsers'],
  endpoints: (build) => ({
    // Set credit for a user (create new credit facility)
    setCredit: build.mutation<CreditApiResponse<any>, SetCreditRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('user_id', data.user_id.toString());
        formData.append('credit_limit', data.credit_limit.toString());
        formData.append('balance', (data.balance || data.credit_limit).toString());
        
        console.log('SetCredit form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.SET_CREDIT,
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: ['UserCredit', 'CreditUsers'],
      transformResponse: (response: any) => {
        console.log('SetCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit set successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('SetCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to set credit',
          status: response.status,
        };
      },
    }),

    // List all users with credit facilities
    listCreditUsers: build.query<CreditApiResponse<CreditUser[]>, void>({
      query: () => {
        const formData = new URLSearchParams();
        const userId = localStorage.getItem('admin_user_id') || '110';
        formData.append('user_id', userId);
        
        console.log('ListCreditUsers form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.LIST_CREDIT_USERS,
          method: 'POST',
          body: formData.toString(),
        };
      },
      providesTags: ['CreditUsers'],
      transformResponse: (response: any) => {
        console.log('ListCreditUsers response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.data || response.result || [],
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('ListCreditUsers error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to load credit users',
          status: response.status,
        };
      },
    }),

    // Update credit limit for existing user
    updateCredit: build.mutation<CreditApiResponse<any>, UpdateCreditRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('user_id', data.user_id.toString());
        formData.append('credit_limit', data.credit_limit.toString());
        if (data.balance !== undefined) {
          formData.append('balance', data.balance.toString());
        }
        
        console.log('UpdateCredit form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.UPDATE_CREDIT,
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: ['UserCredit', 'CreditUsers'],
      transformResponse: (response: any) => {
        console.log('UpdateCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit updated successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('UpdateCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to update credit',
          status: response.status,
        };
      },
    }),

    // Delete credit facility for a user
    deleteCredit: build.mutation<CreditApiResponse<any>, DeleteCreditRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('user_id', data.user_id.toString());
        
        console.log('DeleteCredit form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.DELETE_CREDIT,
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: ['UserCredit', 'CreditUsers'],
      transformResponse: (response: any) => {
        console.log('DeleteCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Credit facility removed successfully',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('DeleteCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to delete credit',
          status: response.status,
        };
      },
    }),

    // Get credit details for specific user
    getUserCredit: build.query<CreditApiResponse<UserCredit>, { user_id: number }>({
      query: ({ user_id }) => {
        const formData = new URLSearchParams();
        formData.append('user_id', user_id.toString());
        
        console.log('GetUserCredit form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.GET_USER_CREDIT,
          method: 'POST',
          body: formData.toString(),
        };
      },
      providesTags: ['UserCredit'],
      transformResponse: (response: any) => {
        console.log('GetUserCredit response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('GetUserCredit error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to get user credit',
          status: response.status,
        };
      },
    }),

    // Search users for credit assignment
    searchUsers: build.query<CreditApiResponse<SearchUser[]>, SearchUserRequest>({
      query: (params) => {
        const formData = new URLSearchParams();
        if (params.search) {
          formData.append('search', params.search);
        }
        formData.append('limit', (params.limit || 10).toString());
        
        console.log('SearchUsers form data:', Object.fromEntries(formData));
        
        return {
          url: CREDIT_API_CONFIG.ENDPOINTS.SEARCH_USERS,
          method: 'POST',
          body: formData.toString(),
        };
      },
      providesTags: ['SearchUsers'],
      transformResponse: (response: any) => {
        console.log('SearchUsers response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.data || response.result || [],
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('SearchUsers error:', response);
        return {
          error: true,
          message: response.data?.message || response.error || 'Failed to search users',
          status: response.status,
        };
      },
    }),
  }),
});

export const {
  useSetCreditMutation,
  useListCreditUsersQuery,
  useUpdateCreditMutation,
  useDeleteCreditMutation,
  useGetUserCreditQuery,
  useSearchUsersQuery, 
} = creditSalesApi;